import { createI18nInfo } from '../../utils/i18n';
import {
  splitIntoGraphme,
  splitIntoSentence,
  splitIntoWords,
} from '../../utils/string';
import {
  ConcreteGame,
  GameData,
  GameScenario,
  GameType,
  I18nGameData,
  TextOrderingSoundMode,
  TextOrderingUnit,
  TextOrderingUnitType,
  TextOrderingVariant,
  TextOrderingWord,
} from '../GameStore';
import { GameSoundType, TtsLocale } from '../Base';
import { GameDraft } from './GameDraft';
import { GameProduct } from './GameProduct';
import { GamePart } from './GamePart';
import SYALLABIFICATION_JSON_DATA from '../../constants/MemoryGames/syllabification.json';

export enum ConvertType {
  FillInTheBlanksBySpelling = 'fillInTheBlanksBySpelling',
  FillInTheBlanksBySyllable = 'fillInTheBlanksBySyllable',
  JoyaPassage = 'joyaPassage',
}

export const gameProductConvertFunc = (convertType: ConvertType) => {
  switch (convertType) {
    case ConvertType.FillInTheBlanksBySpelling:
      return convertFillInTheBlanksBySpellingProduct;
    case ConvertType.FillInTheBlanksBySyllable:
      return convertFillInTheBlanksBySyllableProduct;
    case ConvertType.JoyaPassage:
      return convertJoyaPassageProduct;
  }
};

export const createGamePart = (
  convertType: ConvertType,
  index: number,
): GamePart => {
  const partId = String(index).padStart(2, '0');
  switch (convertType) {
    case ConvertType.FillInTheBlanksBySpelling:
      return {
        index,
        partId: partId,
        name: 'spelling',
        gameType: convertType,
        iconTag: 'spelling',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.FillInTheBlanksBySyllable:
      return {
        index,
        partId: partId,
        name: 'syllable',
        gameType: convertType,
        iconTag: 'syllable',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.JoyaPassage:
      return {
        index,
        partId: partId,
        name: 'joyaPassage',
        gameType: convertType,
        iconTag: 'joyaPassage',
        isRoot: false,
        isSelected: true,
      };
  }
};

const generateTextOrderingProduct = (
  gameDraft: GameDraft,
  wordsResult: TextOrderingWord[],
  {
    draftId,
    partId,
    index,
  }: { draftId: string; partId: string; index: number },
  { answerFirstLetter = false }: { answerFirstLetter?: boolean } = {},
) => {
  const locale = gameDraft.locale;
  const plainText = gameDraft.game.answer.text.text;
  const title = gameDraft.game.question.text.text;

  if (
    wordsResult.every((result) =>
      result.units.every((unit) => unit.type === TextOrderingUnitType.Hint),
    )
  ) {
    return undefined;
  }

  const gameData: GameData<TextOrderingVariant> = {
    title: title,
    description: plainText,
    variants: [
      {
        instruction: {
          text: gameDraft.game.question.text.text,
          sound: gameDraft.game.question.hasTts
            ? {
                type: GameSoundType.Tts,
                locale: gameDraft.game.question.text.soundText
                  ? gameDraft.game.answer.locale
                  : gameDraft.game.question.locale,
                text:
                  gameDraft.game.question.text.soundText ??
                  gameDraft.game.question.text.text,
              }
            : undefined,
        },
        answer: {
          words: wordsResult,
          locale: gameDraft.game.answer.locale,
          soundMode: gameDraft.game.answer.hasTts
            ? TextOrderingSoundMode.Normal
            : TextOrderingSoundMode.Silent,
          answerFirstLetter: answerFirstLetter,
          isLyrics: false,
        },
      },
    ],
  };

  const i18nGameData: I18nGameData<TextOrderingVariant> = createI18nInfo(
    locale,
    gameData,
  );

  const game = new ConcreteGame({
    gameId: draftId,
    gameCollectionId: 'gameCollectionId',
    classroomId: 'classroomId',
    createdBy: 'createdBy',
    createdAt: new Date(),
    lastModifiedAt: new Date(),
    version: '1.0',
    timeLimit: 'auto',
    i18nGameData: i18nGameData,
    gameType: GameType.TextOrdering,
    scenario: GameScenario.Default,
    dependencies: {
      images: [],
      audios: [],
      videos: [],
      resourceImages: [],
      strokes: [],
      ttsAudios: [],
    },
  });

  const gameProduct: GameProduct = {
    draftId: draftId,
    partId: partId,
    isSelected: true,
    index: index,
    game: game,
    title: title,
    description: plainText.slice(0, 32),
  };

  return gameProduct;
};

const convertFillInTheBlanksBySpellingProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  const wordsResult: TextOrderingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitWords = splitIntoWords(sentence.text, locale);

    const selectWord: TextOrderingUnit[] = [];
    splitWords.forEach((_, index) => {
      const word = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      if (word) {
        if (word.type === TextOrderingUnitType.Blank) {
          const graphmes = splitIntoGraphme(word.key, locale);
          graphmes.forEach((graphme) => {
            selectWord.push({
              key: graphme.text,
              type: TextOrderingUnitType.Blank,
            });
          });
        } else {
          selectWord.push(word);
        }
      }
    });
    startIndex = startIndex + splitWords.length;

    wordsResult.push({ word: sentence.text, units: selectWord });
  });

  return generateTextOrderingProduct(gameDraft, wordsResult, {
    draftId,
    partId,
    index: gameIndex,
  });
};

const convertFillInTheBlanksBySyllableProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  if (
    !([TtsLocale.AU, TtsLocale.GB, TtsLocale.US] as TtsLocale[]).includes(
      gameDraft.locale,
    )
  ) {
    return undefined;
  }
  const wordsResult: TextOrderingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitWords = splitIntoWords(sentence.text, locale);

    const selectWord: TextOrderingUnit[] = [];
    splitWords.forEach((_, index) => {
      const word = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      if (word) {
        if (word.type === TextOrderingUnitType.Blank) {
          const SYALLABIFICATION: Record<string, string> =
            SYALLABIFICATION_JSON_DATA;

          const wordKey = word.key.toLowerCase();
          const syllable = SYALLABIFICATION[wordKey];

          if (syllable) {
            const syllables = syllable.split('-');
            if (syllables.length > 1) {
              let syllableStartIndex = 0;
              syllables.forEach((s) => {
                const originalSyllable = word.key.substring(
                  syllableStartIndex,
                  syllableStartIndex + s.length,
                );
                syllableStartIndex += s.length;
                selectWord.push({
                  key: originalSyllable,
                  type: TextOrderingUnitType.Blank,
                });
              });
            } else {
              selectWord.push({
                key: word.key,
                type: TextOrderingUnitType.Hint,
              });
            }
          } else {
            selectWord.push({
              key: word.key,
              type: TextOrderingUnitType.Hint,
            });
          }
        } else {
          selectWord.push(word);
        }
      }
    });
    startIndex = startIndex + splitWords.length;

    wordsResult.push({ word: sentence.text, units: selectWord });
  });

  return generateTextOrderingProduct(gameDraft, wordsResult, {
    draftId,
    partId,
    index: gameIndex,
  });
};

const convertJoyaPassageProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  const wordsResult: TextOrderingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitWords = splitIntoWords(sentence.text, locale);

    const selectWord: TextOrderingUnit[] = [];
    splitWords.forEach((_, index) => {
      const word = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      if (word) {
        selectWord.push(word);
      }
    });
    startIndex = startIndex + splitWords.length;

    wordsResult.push({ word: sentence.text, units: selectWord });
  });

  return generateTextOrderingProduct(
    gameDraft,
    wordsResult,
    {
      draftId,
      partId,
      index: gameIndex,
    },
    {
      answerFirstLetter: true,
    },
  );
};
